export class FinDocViewModel {
    id = 0;

    propertyId: number | null = null;
    reservationId: number | null = null;

    documentStatusId = 1;
    documentTypeId = 1;

    documentIdentifier = "";
    documentInternalIdentifier = "";

    discountPercent = 0; // obsolete - only shown on existing invoices, not editable

    note = "";
    taxExemptionReason = "";

    workingCurrencyId = 2; // EUR
    workingCurrencyISO3 = "EUR";
    workingToFormalConversionRate = 1;

    formalCurrencyId: number | null = null;
    formalCurrencyISO3 = "";
    formalToWorkingConversionRate = 1;

    // sub models
    basicInfo: FinDocBasicInfoViewModel = new FinDocBasicInfoViewModel();
    clientInfo: FinDocClientInfoViewModel = new FinDocClientInfoViewModel();
    lineItems: FinDocLineItemViewModel[] = [];
    relatedAdvanceInvoices: FinDocRelatedAdvanceViewModel[] = [];
    unassignedAdvanceInvoices: FinDocRelatedAdvanceViewModel[] = [];
    unassignedPayments: PaymentModel[] = [];

    fiscalizationFailed = false;
}

export class FinDocBasicInfoViewModel {
    issueDate = "";
    deliveryDate: string | null = null;
    dueDate: string | null = null;

    servicePeriodFromDate: string | null = null;
    servicePeriodToDate: string | null = null;

    issuePlace = "";

    businessUnitId: number | null = null;
    cashRegisterId: number | null = null;
    paymentMeansId: number | null = null;
    cardTypeId: number | null = null;

    isChannexIdPresent = false;
    hasSavedCreditCard = false;

    referenceDocumentId: number | null = null;
    referenceDocumentTypeId: number | null = null;

    referenceNumber = "";

    userChangedDates = false;
    showOwnerDetails = false;

    paymentMeansAdditionalDetails: PaymentMeansAdditionalDetailsModel[] = [];
}

export interface PaymentMeansAdditionalDetailsModel {
    id: number;
    paymentMeansId: number | null;
    cardTypeId: number | null;
    currencyId: number | null;
    amount: number | null;
}

export class FinDocClientInfoViewModel {
    id = 0;
    name = "";
    identificationDocumentNumber = "";
    businessId = "";
    address = "";
    postalCode = "";
    city = "";
    countryId: number | null = null;
    countryName = "";
    email = "";
    phone = "";

    isLegalEntity = false;
}

export interface FinDocRelatedAdvanceViewModel {
    id: number;
    documentIdentifier: string;
    documentInternalIdentifier: string;
    issueDate: string;

    workingCurrencyId: number;
    totalDueAmount: number;

    formalCurrencyId: null | number;
    totalDueAmountFormalCurrency: null | number;
}

export class FinDocLineItemViewModel {
    id = 0;
    itemName = "";
    itemNumber = 1;
    financialDocumentId = 0;
    reservationAccUnitTypeId: number | null = null;
    resAccUnitTypeAddOnServiceId: number | null = null;
    addOnServiceId: number | null = null;

    unitOfMeasureId = 1;
    decimalQuantity = 1;
    taxRateId = 0;
    consumptionTaxRate: number | null = null;
    grossUnitPrice = 0;
    discountPercent = 0;

    taxExemptionReasonFromAOS = "";

    sourceFinancialDocumentId: number | null = null;
    sourceLineItemId: number | null = null;

    isCityTax = false;
    isStayCancellation = false;
    isFoodAndBeverages = false;

    ownerId: number | null = null;
    ownerIsLegalEntity: boolean | null = null;
    ownerPaysVAT: boolean | null = null;
    ownerIsResident = false;
    ownerVatId = "";
    agencyWorkModelId: number | null = null;

    isExchangeRateDifference = false;

    isGroupedStayLineItem: boolean | null = null;

    // for display only (not fetched from server)
    grossPrice = 0;
    netUnitPrice = 0;
    netPrice = 0;

    grossUnitPriceAlt = 0;
    grossPriceAlt = 0;
    netUnitPriceAlt = 0;
    netPriceAlt = 0;

    netPriceCorrectionAmount = 0;
    netPriceCorrectionAmountAlt = 0;
}

export class PaymentModel {
    id = 0 as number | null;
    paymentDate = "";
    amount = 0;
    remainingAmount = 0;
    amountToAsign = 0;
    currencyId: number | null = null;
    currencyCode = "";
    paymentMeansId: number | null = null;
    cardTypeId: number | null = null;
    alreadyPaidForFinDoc: number | null = null;
}

export interface OwnersInvoiceBulkCreateResult {
    id: number;
    locked: boolean;
    fiscalized: boolean;
    pdfGenerated: boolean;
}

export interface FinancialDocumentCreateResponseModel {
    documentId: number;
    advanceInvoiceCancellationResults: CancelAdvanceInvoiceResponse[];
    isSuccess: boolean;
}

export interface FinancialDocumentSaveLockAndMarkAsPaidResult {
    documentId: number;
    isSuccess: boolean;
    saved: boolean;
    locked: boolean;
    fiscalized: boolean;
    paid: boolean;
    pdfGenerated: boolean;
    errorMessage: string;
    advanceInvoiceCancellationResults: CancelAdvanceInvoiceResponse[];
}

export interface CreateAdvanceInvoiceForPaymentsResultModel {
    documentId: number;
    advanceInvoiceCancellationResults: CancelAdvanceInvoiceResponse[];
    isSuccess: boolean;
    exception: string;
    paymentmeansId: number;
    saved: boolean;
    locked: boolean;
    fiscalized: boolean;
    paid: boolean;
    pdfGenerated: boolean;
}

export interface CancelAdvanceInvoiceResponse {
    originalDocumentId: number;
    originalDocumentIdentifier: string;
    originalDocumentInternalIdentifier: string;
    stornoCreationFailed: boolean;
    stornoLockFailed: boolean;
    stornoPaymentsFailed: boolean;
    stornoFiscalizationFailed: boolean;
}

export interface FinancialDocumentLockResult {
    locked: boolean;
    fiscalized: boolean;
    pdfGenerated: boolean;
    errorMessage: string;
}

export interface FinancialDocumentCreateResponseModel {
    documentId: number;
    advanceInvoiceCancellationResults: CancelAdvanceInvoiceResponse[];
    isSuccess: boolean;
}

export interface CancelAdvanceInvoiceResponse {
    originalDocumentId: number;
    originalDocumentNumberComplete: string;
    stornoCreationFailed: boolean;
    stornoLockFailed: boolean;
    stornoPaymentsFailed: boolean;
    stornoFiscalizationFailed: boolean;
}

export interface FinancialDocumentFiscalizeResult {
    fiscalized: boolean;
    errorMessage: string;
}

export interface GenerateInvoiceResultModel {
    documentId: number;
    warnings: string[];
}

export interface GenerateInvoiceResultModelBulk {
    documentIds: number[];
    warnings: string[];
}

export interface TaxRecapCalculationResultModel {
    hasError: boolean;
    items: TaxRecapViewModel[];
    consumptionTaxRecap: ConsumptionTaxRecapViewModel[];
}

export interface TaxRecapViewModel {
    taxRateId: number;
    taxRateTextResourceKey: string;

    baseAmount: number;
    taxAmount: number;
    consumptionTaxBaseAmount: number | null;
    consumptionTaxAmount: number | null;

    hasBaseAmountError: boolean;
    hasTaxAmountError: boolean;

    recapItems: TaxRecapItemViewModel[];
}

export interface ConsumptionTaxRecapViewModel {
    consumptionTaxRate: number;
    baseAmount: number;
    taxAmount: number;
    hasBaseAmountError: boolean;
    hasTaxAmountError: boolean;
    recapItems: TaxRecapItemViewModel[];
}

export interface TaxRecapItemViewModel {
    taxRateId: number;

    originDocumentIdentifier: string;
    originDocumentIssueDate: string;
    originDocumentTotalWithVat: number | null;

    baseAmount: number;
    taxAmount: number;
    consumptionTaxBaseAmount: number | null;
    consumptionTaxAmount: number | null;
}

export interface CancellationPolicy {
    reservationId: number;
    reservationAccUnitTypeId?: number;
    amount: number;
}

export interface PaymentSlipViewModel {
    id: number;
    documentNumber: string;
    documentIdentifier: string;
    documentInternalIdentifier: string;
    documentTypeId: number;
    transactionDateTime: string;
    customerId: number;
    customerName: string;
    transactionAmount: number;
    currencyId: number;
    transactionDescriptionId: number | null;
    additionalNoteForTransaction: string;
    cashRegisterId: number | null;
    businessUnitId: number | null;
}

export interface PaymentSlipSaveResult {
    id: number;
    locked: boolean;
    pdfGenerated: boolean;
}